import { ProfileAdditionalData, ProfileAdditionalDataItem, ProfileAdditionalDataRecord, profileAdditionalDataSchemaRecord } from "@bonusmap/api-types";
import { BaseWebComponent } from "../base-components/base-web-component";
import { ProfileAdditionalDataItem as ProfileAdditionalDataItemControl } from "./profile-additional-data-item";
import { isNumber, templateParse } from "@bonusmap/shared_ui/library";
export class ProfileAdditionalDataItemList extends BaseWebComponent {
    static webComponentName: string = "profile-additional-data-item-list"
    static observedAttributes = []; // Nessun attributo da osservare per ora
    protected _templateId: string = "profile-additional-data-item-list";
    private suspendValidation:boolean=false;
    constructor() {
        super();
        this.globalEventListeners();
    }
    globalEventListeners() {
        this.on('click', '[role="remove-item"]', (event: Event) => {
            event.preventDefault();
            const button: HTMLElement = event.target as HTMLElement;
            const itemElement = button.parentElement.querySelector('profile-additional-data-item');
            if (itemElement) {
                const itemValue: ProfileAdditionalDataRecord = (itemElement as ProfileAdditionalDataItemControl).value;
                if (itemValue) {
                    this.value = this.value.filter(item => !(item.additional_id === itemValue.additional_id && item.value === itemValue.value));
                    this.render();
                    this.dispatch('value-changed', { origin: this });
                }
            }
        });
        this.on('click', '[role="add-item"]', (event: Event) => {
            event.preventDefault();
            this.suspendValidation=true;
            // Aggiunge un nuovo elemento con ID 0 e valore vuoto
            const newItem: ProfileAdditionalDataRecord = {
                additional_id: 0,
                value: ''
            };
            this.value = [...this.value, newItem];
            this.render();
            this.dispatch('value-changed', { origin: this });
            this.suspendValidation=false;
        });
        this.addEventListener('value-item-changed', (event: Event) => {
            event.stopPropagation(); // Prevent event from bubbling up
            this.value = this.getValueFromItemControls();
            this.dispatch('value-changed', { origin: this });
        });
    }
    private additionalsPicker!: HTMLSelectElement;
    private _value: ProfileAdditionalDataRecord[];
    get readOnly(): boolean {
        return this.hasAttribute('readonly') || this.closest('[readonly]') !== null;
    }

    private isValidValueItem(value: any): boolean {
        // Validazione del singolo record
        const result = profileAdditionalDataSchemaRecord.safeParse(value);
        const validId = this.suspendValidation || isNumber(value.additional_id) && value.additional_id>0;
        return result.success && validId;
    }
    private filterValidValues(value: any): ProfileAdditionalDataRecord[] {
        // Filtra e restituisce solo i record validi
        if (!Array.isArray(value)) {
            return [];
        }
        return value.filter(item => this.isValidValueItem(item));
    }
    private _getValueInitialized(): ProfileAdditionalDataRecord[] {
        // prende il valore dallo script tag se non è già stato fatto
        const scriptTag = this.querySelector('script[type="application/json"]');
        if (scriptTag) {
            try {
                const parsed = JSON.parse(scriptTag.textContent || '[]');
                if (this.filterValidValues(parsed)) {
                    return parsed;
                }
            } catch (e) { }
        }
        return [];
    }
    public get value(): ProfileAdditionalDataRecord[] {
        if (!this._value) {
            this._value = this._getValueInitialized();
        }
        // [debug] console.log(`get value stato validazione sospeso:`, this.suspendValidation);
        return this.filterValidValues(this._value);
    }
    private needsReRender(oldValue:ProfileAdditionalDataRecord[],newValue:ProfileAdditionalDataRecord[]): boolean {
        if(!oldValue || !newValue) return true;
        if(oldValue.length!==newValue.length) return true; 
        // a questo punto il re-render scatta solo se gli additional_id sono diversi
        const oldIds=oldValue.map(item=>item.additional_id).sort();
        const newIds=newValue.map(item=>item.additional_id).sort();
        for(let i=0;i<oldIds.length;i++){
            if(oldIds[i]!==newIds[i]) return true;
        }
        return false;
    }
    public set value(value: ProfileAdditionalDataRecord[]) {
        value=this.filterValidValues(value);
        const oldValue = this._value;
        this._value = value;
        // if(this.needsReRender(oldValue,value)){
        //     this.render();
        // }
    }

    private getValueFromItemControls(): ProfileAdditionalDataRecord[] {
        const itemControls = Array.from(this.querySelectorAll('profile-additional-data-item')) as ProfileAdditionalDataItemControl[];
        return itemControls
        .map(item => item.value)
        .filter(item => this.isValidValueItem(item)) as ProfileAdditionalDataRecord[];
    }

    // Restituisce gli ID già in uso come stringhe per la query
    private get idsInUse(): string[] {
        return this.value.map(item => item.additional_id).map(item => item.toString());
    }
    private renderPickerOptions(values: ProfileAdditionalDataItem[]): void {
        this.clearPickerOptions();
        values.forEach(item => {
            const option = document.createElement('option');
            option.value = item.id.toString();
            option.textContent = item.label ?? item.name;
            this.additionalsPicker.appendChild(option);
        });
    }
    private clearPickerOptions(): void {
        // Rimuove tutte le opzioni esistenti tranne la prima
        const options = Array.from(this.additionalsPicker.options);
        options.forEach((option, index) => {
            if (index > 0) {
                this.additionalsPicker.removeChild(option);
            }
        });
    }
    private async fetchByIds(): Promise<ProfileAdditionalDataItem[]> {
        const ids = this.idsInUse;
        if (ids.length === 0) return [];
        const result = await window.api.manager.profilesAdditionalData.getByIds(ids.map(id => parseInt(id, 10)));
        if (result.success && result.data) {
            const dataset = result.data as ProfileAdditionalData[];
            const items = this.value ;
            // costruisce un array di ProfileAdditionalDataItem basato sugli ID in uso e i dati ottenuti    
            const itemsResult: ProfileAdditionalDataItem[] = [];
            dataset.forEach(data => {
                if (data && data.id) {
                    const value = items.find(i => i.additional_id === data.id)?.value || '';
                    itemsResult.push( {
                        id: Number(data.id),
                        label: data.label,
                        name: data.name,
                        description: data.description,
                        organization_id: data.organization_id,
                        value
                    });
                }
            });
            return itemsResult;
        }
        return [];
    }
    private async fetchAndRenderPickerOptions(): Promise<void> {
        const idsInUse = this.idsInUse;
        try {
            let whereClause = idsInUse.length > 0 ? `id NOT IN (${idsInUse.join(',')})` : '1=1';
            const result = await window.api.manager.profilesAdditionalData.query(whereClause, [], 'label', 100);
            if (result.success && result.data) {
                this.renderPickerOptions(result.data as ProfileAdditionalDataItem[]);
            } else {
                console.error("Failed to fetch additional data items:", result.error);
            }
        } catch (error) {
            console.error("Error fetching additional data items:", error);
        }
    }
    protected buildRenderingData(): Record<string, any> {
        const data = super.buildRenderingData();
        return {
            ...data,
            // Aggiungi dati specifici per il template qui  
            isAdmin:this.isAdmin,
            items: this.value
        }
    }
    protected override afterRender(): void {
        super.afterRender();
        this.additionalsPicker = this.roleElementSelector('additionals-pick') as HTMLSelectElement;
        this.additionalsPicker.addEventListener('change', (event: Event) => {
            const select = event.target as HTMLSelectElement;
            const selectedValue = select.value;
            if (selectedValue) {
                const additionalId = parseInt(selectedValue, 10);
                if (!isNaN(additionalId) && additionalId > 0) {
                    const newItem: ProfileAdditionalDataRecord = {
                        additional_id: additionalId,
                        value: ''
                    };
                    this.value = [...this.value, newItem];
                    this.render();
                    this.dispatch('value-changed', { origin: this });
                }
                // Reimposta il selettore
                select.value = '';
            }
        });
        this.fetchAndRenderPickerOptions();
    }
    async renderReadOnly(): Promise<void> {
        const items = await this.fetchByIds();
        this.renderingData = {
            ...(this.buildRenderingData()),
            readOnly: true,
            items
        };
        this.innerHTML = templateParse(this.templateId, this.renderingData, this.nunjucksEnvCallbacks);
    }
    protected render(): void {
        if (this.readOnly) {
            this.renderReadOnly();
        } else {
            super.render();
        }
    }
}